import { Dropdown } from "@/components/Dropdown";
import DropdownItem from "@/components/Dropdown/DropdownItem";
import { getJWTForAccountSuccessCallback } from "@/requests/callbacks/user";
import {
  useGetJWTForAccount,
  usePostAddCustomer,
  useRelatedCustomers,
  useUser,
} from "@/requests/hooks/user";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import {
  faBuilding,
  faCirclePlus,
  faSpinnerThird,
} from "@fortawesome/pro-duotone-svg-icons";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    desktopMainContainer: "hidden sm:flex",
    mobileMainContainer: "flex sm:hidden",
    parentContainer:
      "flex sm:min-w-fit flex-row items-center justify-between gap-10 sm:gap-20",
    iconContainer:
      "flex h-50 w-50 items-center justify-center rounded-full bg-white text-primary",
    imageContainer: "h-50 w-50",
    nameContainer: "hidden sm:flex",
    mobileContainer:
      "fixed inset-0 mt-90 flex min-h-[calc(100vh-80px)] flex-col justify-between overscroll-contain bg-white sm:hidden",
    mobileAccountsContainer: "flex-grow overflow-y-scroll",
    navItem: "flex flex-col border-b border-gray-light p-20",
    navItemRow: "flex flex-row items-center gap-20",
    addCompanySlot:
      "cursor-pointer border-t border-t-secondary bg-white shadow",
  },
});

interface IProps {
  activeOpenMenu: IOpenMenuType;
  toggleMenu: () => void;
}

const AccountSwitcher = ({ activeOpenMenu, toggleMenu }: IProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: relatedCustomers, isLoading: isGetRelatedCustomersLoading } =
    useRelatedCustomers();

  const {
    mutate: postAddCustomer,
    data: postAddCustomerData,
    isSuccess: isAddCustomerSuccess,
    isPending: isAddCustomerPending,
  } = usePostAddCustomer();

  const {
    mutate: switchAccount,
    isSuccess: isGetJWTForAccountSuccess,
    isPending: isGetJWTForAccountPending,
  } = useGetJWTForAccount();
  const { data: user } = useUser();

  const {
    desktopMainContainer,
    mobileMainContainer,
    parentContainer,
    iconContainer,
    imageContainer,
    nameContainer,
    mobileContainer,
    mobileAccountsContainer,
    navItem,
    navItemRow,
    addCompanySlot,
  } = styles();

  useEffect(() => {
    if (isGetJWTForAccountSuccess) {
      getJWTForAccountSuccessCallback(queryClient, navigateHome);
    }
  }, [isGetJWTForAccountSuccess]);

  useEffect(() => {
    if (isAddCustomerSuccess && !isGetJWTForAccountPending)
      switchAccount({ merchant_id: postAddCustomerData?.merchantId });
  }, [isAddCustomerSuccess]);

  const navigateHome = () => navigate(0);

  const triggerSwitchAccount = (merchant_id: number) =>
    switchAccount({ merchant_id });

  const createDummyCustomerAndNavigate = () => {
    postAddCustomer({
      companyName: "Placeholder Company Name",
      companyIndustry: "B2C",
      companyWebsiteUrl: "https://example.com",
    });
  };

  const renderActiveAccount = () => {
    const activeAccount = relatedCustomers?.results.find(
      account => !!account.active,
    );

    const renderLogoImageOrIcon = () => {
      if (activeAccount && !!activeAccount.logo && !isGetJWTForAccountPending) {
        return <img src={activeAccount.logo} className={imageContainer()} />;
      }

      return (
        <div className={iconContainer()}>
          <FontAwesomeIcon
            fixedWidth
            icon={
              isGetRelatedCustomersLoading || isGetJWTForAccountPending
                ? faSpinnerThird
                : faBuilding
            }
            spin={isGetRelatedCustomersLoading || isGetJWTForAccountPending}
            className="h-25 w-25"
          />
        </div>
      );
    };

    return (
      <div className={parentContainer()}>
        {renderLogoImageOrIcon()}
        <span className={nameContainer()}>
          {activeAccount?.companyName || t("generic.loading")}
        </span>
        <FontAwesomeIcon fixedWidth icon={faCaretDown} />
      </div>
    );
  };

  const renderAddCompany = () => (
    <div className={addCompanySlot()}>
      <DropdownItem
        key="add-icon"
        leadingIcon={isAddCustomerPending ? faSpinnerThird : faCirclePlus}
        text={
          isAddCustomerPending
            ? t("components.accountSwitcher.loading")
            : t("components.accountSwitcher.addCompany")
        }
        onClick={createDummyCustomerAndNavigate}
        iconProps={{ spin: isAddCustomerPending }}
      />
    </div>
  );

  const renderAccounts = () => {
    if (isGetRelatedCustomersLoading || !relatedCustomers?.results) return [];

    if (doesUserHaveScope("JITA", user?.scopes))
      return relatedCustomers.results
        .filter(result => result.active)
        .map(account => ({
          leadingIcon: faBuilding,
          text: account.companyName,
          onClick: () => triggerSwitchAccount(account.merchantId),
        }));

    return relatedCustomers.results.map(account => ({
      leadingIcon: faBuilding,
      text: account.companyName,
      onClick: () => triggerSwitchAccount(account.merchantId),
    }));
  };

  const renderDesktopDropdown = () => (
    <div className={desktopMainContainer()}>
      <Dropdown
        button={{
          override: renderActiveAccount(),
        }}
        items={renderAccounts()}
        testId="account-switcher-dropdown"
        trailingStaticSlot={renderAddCompany()}
      />
    </div>
  );

  const renderMobileAccountsList = () => {
    if (isGetRelatedCustomersLoading || !relatedCustomers?.results) return null;

    return relatedCustomers.results.map((account, index) => (
      <div
        key={index}
        className={navItem()}
        onClick={() => triggerSwitchAccount(account.merchantId)}
        data-testid={account.companyName}>
        <div className={navItemRow()}>
          <FontAwesomeIcon
            fixedWidth
            icon={faBuilding}
            className="text-primary"
          />
          {account.companyName}
        </div>
      </div>
    ));
  };

  const renderMobileMenu = () => (
    <div
      className={mobileMainContainer()}
      data-testid="account-switcher-dropdown">
      <div onClick={toggleMenu}>{renderActiveAccount()}</div>
      {activeOpenMenu === "ACCOUNT_SWITCHER" && (
        <div className={mobileContainer()}>
          <div className={mobileAccountsContainer()}>
            {renderMobileAccountsList()}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {renderDesktopDropdown()}
      {renderMobileMenu()}
    </>
  );
};

export default AccountSwitcher;
